import { components } from "@certn/api/generated-schemas/client-portal";
import { showToast } from "@certn/cascadia";
import { calculateToastTimeout } from "@certn/utils";
import type { TFunction } from "i18next";

import {
  NotificationChannel,
  Order,
  Package,
  SelectedBundlesType,
  SelectedChecksType,
} from "./OrderContext";
import { CheckData } from "./types";
import { UserDetails } from "../../types";
import {
  resolvePriceIsCatalogMinimum,
  resolvePriceIsCatalogMinimumFromOptions,
} from "../../utils/resolvePriceIsCatalogMinimum";
import uniqBy from "lodash/uniqBy";

export function isCheckStandaloneDisabled(
  orderChecks: any,
  check: Pick<SelectedChecksType, "must_be_ordered_with_one_of">
) {
  if (
    check.must_be_ordered_with_one_of === undefined ||
    check.must_be_ordered_with_one_of.length === 0
  )
    return false;

  return !orderChecks.some((orderCheck: { key: string }) =>
    check.must_be_ordered_with_one_of?.includes(orderCheck.key)
  );
}

function getCustomerGroupId(order: Order, user?: UserDetails) {
  if (order && order.group) {
    return order.group;
  }
  if (user && user.customer_groups.length > 0) {
    return user.customer_groups[0].id;
  }
  return null;
}

function getCheckTypesWithArguments(
  bundles: SelectedBundlesType[],
  checks: SelectedChecksType[],
  packageData?: Package
) {
  if (packageData && packageData.checks?.length) {
    return Object.fromEntries(
      packageData.checks.map(({ id, check_arguments }) => [
        id,
        check_arguments || {},
      ])
    );
  }
  const checksWithArguments = checks.map(
    ({ key, check_arguments, parent_type, ordering_type }) => {
      const effectiveKey = parent_type ?? key;

      const args = {
        ...(check_arguments || {}),
        ...(parent_type ? { ordering_type, explicit_check_type: [key] } : {}),
      };

      return [effectiveKey, args];
    }
  );
  const bundleChecks = bundles.flatMap(({ checks }) => checks || []);
  bundleChecks.forEach(({ type, check_arguments }) => {
    if (checks.find((check) => check.key.includes(type))) {
      return;
    }
    checksWithArguments.push([type, check_arguments]);
  });

  return Object.fromEntries(checksWithArguments);
}

export function enabledNotificationChannels(flags: {
  applicantSmsMessaging?: boolean;
  applicantWhatsappMessaging?: boolean;
}): NotificationChannel[] {
  const channels: NotificationChannel[] = [];
  if (flags.applicantSmsMessaging) {
    channels.push("SMS");
  }
  if (flags.applicantWhatsappMessaging) {
    channels.push("WHATSAPP");
  }
  return channels;
}

export function selectedEnabledNotificationChannels(
  selected: NotificationChannel[] | undefined,
  enabled: NotificationChannel[]
): NotificationChannel[] {
  const enabledSet = new Set(enabled);
  return (selected ?? []).filter((channel) => enabledSet.has(channel));
}

function applicantMobileNumbersForSubmit(
  order: Order,
  channels: NotificationChannel[]
): Record<string, string> {
  if (channels.length === 0) {
    return {};
  }
  const numbers: Record<string, string> = {};
  for (const email of order.recipients) {
    const entry = order.recipient_mobile_numbers?.[email];
    if (entry?.status === "valid" && entry.number) {
      numbers[email] = entry.number;
    }
  }
  return numbers;
}

export function orderContextToSubmitOrderPayload(
  order: Order,
  user?: UserDetails,
  options?: {
    consumerReportCertificationAcknowledged?: boolean;
    enabledNotificationChannels?: NotificationChannel[];
  }
) {
  const notificationChannels = selectedEnabledNotificationChannels(
    order.notification_channels,
    options?.enabledNotificationChannels ?? []
  );

  return {
    message: order.message,
    applicant_emails: order.recipients,
    check_types_with_arguments: getCheckTypesWithArguments(
      order.bundles,
      order.checks,
      order.packageData
    ),
    customer_group: getCustomerGroupId(order, user),
    tags: order.tags,
    client_form_data: order.clientFormDataAndErrors.data,
    bundles: order.packageData?.bundles || order.bundles.map(({ id }) => id),
    package: order.packageData?.id,
    rescreen: order.rescreenFrequency,
    applicant_language: order.applicant_language,
    ...(options?.consumerReportCertificationAcknowledged
      ? { consumer_report_certification_acknowledged: true }
      : {}),
    cv_review_id: order.cv_review_id,
    ...(notificationChannels.length > 0
      ? {
          notification_channels: notificationChannels,
          applicant_mobile_numbers: applicantMobileNumbersForSubmit(
            order,
            notificationChannels
          ),
        }
      : {}),
  };
}

/**
 * Resolve the applicant language for a quickscreen case.
 *
 * Quickscreen has no language selector — the client fills out the application
 * themselves — so the consent/document language should follow the client's
 * active UI language. French (the only non-English language in
 * settings.LANGUAGES) is mapped to fr-CA to trigger the French consent
 * templates; every English variant and any unknown locale returns null so the
 * backend applies its region default (settings.LANGUAGE_CODE) rather than being
 * forced to a specific English variant (e.g. forcing en-US on an AU/GB region).
 *
 * An explicit order.applicant_language, if ever set, always takes precedence.
 */
export function resolveQuickscreenApplicantLanguage(
  order: Order,
  uiLanguage?: string
): string | null {
  if (order.applicant_language) {
    return order.applicant_language;
  }
  if (uiLanguage && uiLanguage.toLowerCase().startsWith("fr")) {
    return "fr-CA";
  }
  return null;
}

export function orderContextToQuickscreenPayload(
  order: Order,
  user?: UserDetails,
  uiLanguage?: string
) {
  return {
    applicant_email: order.recipients[0],
    check_types_with_arguments: getCheckTypesWithArguments(
      order.bundles,
      order.checks,
      order.packageData
    ),
    customer_group: getCustomerGroupId(order, user),
    tags: order.tags,
    bundles: order.bundles.map(({ id }) => id),
    client_form_data: order.clientFormDataAndErrors.data,
    applicant_language: resolveQuickscreenApplicantLanguage(order, uiLanguage),
    cv_review_id: order.cv_review_id,
  };
}

export function orderContextToGetClientFormPayload(order: Order) {
  return getCheckTypesWithArguments(
    order.bundles,
    order.checks,
    order.packageData
  );
}

export function getRequiredChecksFromSelectedChecks(
  selectedChecks: SelectedChecksType[] | []
): string[] {
  if (selectedChecks.length === 0) {
    return [];
  }
  const requiredChecks = selectedChecks.reduce<string[]>((acc, check) => {
    return [...acc, ...(check.required_check_types || [])];
  }, []);
  return requiredChecks;
}

export function getAvailableChildChecks(
  children: components["schemas"]["CheckListChild"][]
) {
  return children?.filter((child) => {
    const errorType = child.check_has_ordering_error_for_customer_group?.type;
    return errorType !== "error" && errorType !== "warning";
  });
}

export function getAvailableSelectOptions(
  selectOptions: components["schemas"]["UmbrellaCheckSelectOption"][]
) {
  return selectOptions?.filter(
    (option) => !option.option_has_ordering_error_for_customer_group
  );
}

/* If the check is an umbrella check, then use the price of its most appropriate child check. */
export function getCheckPrice(
  check: CheckData,
  selectedOption?: components["schemas"]["UmbrellaCheckSelectOption"]
): number {
  return (
    selectedOption?.price ??
    getAvailableSelectOptions(check.select_options ?? [])?.[0]?.price ??
    getAvailableChildChecks(check.children ?? [])?.[0]?.price ??
    check.price
  );
}

/**
 * Updates only check_arguments for a given check without recalculating cost or
 * touching selectedOptions. Used for add-on toggles (e.g. CV Review) where the
 * selection/pricing state should remain unchanged.
 */
export function updateCheckArguments(
  selectedChecks: SelectedChecksType[],
  checkType: string,
  checkArguments: Record<string, unknown>
): SelectedChecksType[] {
  return selectedChecks.map((selectedCheck) => {
    if (selectedCheck.key === checkType) {
      return { ...selectedCheck, check_arguments: checkArguments };
    }
    return selectedCheck;
  });
}

/**
 * After updating one check's arguments, propagate any mutually inclusive
 * add-on flags (e.g. include_cv_review) to sibling checks that declare
 * the same add-on key in their add_on_options.
 */
export const syncMutuallyInclusiveAddOns = <
  T extends Pick<
    SelectedChecksType,
    "key" | "check_arguments" | "add_on_options"
  >,
>(
  checks: T[],
  sourceCheckType: string,
  sourceArgs: Record<string, unknown>
): T[] => {
  const addOnKeys = Object.entries(sourceArgs).filter(
    ([, value]) => typeof value === "boolean"
  );
  if (addOnKeys.length === 0) return checks;

  return checks.map((c) => {
    if (c.key === sourceCheckType) return c;

    const updates: Record<string, unknown> = {};
    for (const [key, value] of addOnKeys) {
      if (c.add_on_options?.some((a) => a.key === key)) {
        updates[key] = value;
      }
    }
    if (Object.keys(updates).length === 0) return c;

    return {
      ...c,
      check_arguments: { ...c.check_arguments, ...updates },
    };
  });
};

/*
  Get new set of selected checks based on the currently selected checks
  plus all required checks of each selected check as well as the required checks
  of the selected option for that check.
*/
export function getUpdatedChecks(
  check_arguments: Record<string, unknown>,
  availableChecks: CheckData[] | [],
  selectedChecks: SelectedChecksType[],
  check: CheckData,
  selectedOptions?: components["schemas"]["UmbrellaCheckSelectOption"][]
): SelectedChecksType[] {
  const userSelectedChecks = selectedChecks.map((selectedCheck) => {
    if (selectedCheck.key === check.type) {
      // Merge with existing check_arguments so independently set add-on
      // state (e.g. include_cv_review) is preserved when selection args change.
      const updated_check_arguments = {
        ...selectedCheck.check_arguments,
        ...check_arguments,
      };
      if (
        !("template_id" in check_arguments) ||
        check_arguments.template_id == null
      ) {
        delete updated_check_arguments.template_id;
      }

      if (selectedOptions) {
        const totalCost = selectedOptions.reduce(
          (acc, option) => acc + getCheckPrice(check, option),
          0
        );

        return {
          ...selectedCheck,
          check_arguments: updated_check_arguments,
          cost: totalCost,
          required_check_types: [
            ...(check.required_check_types || []),
            ...(selectedOptions?.flatMap(
              (option) => option.required_check_types ?? []
            ) ?? []),
          ],
          must_be_ordered_with_one_of: check.must_be_ordered_with_one_of,
          parent_type: check.parent_check_type || undefined,
          ordering_type: check.ordering_type,
          price_is_catalog_minimum: resolvePriceIsCatalogMinimumFromOptions(
            check,
            selectedOptions
          ),
          selectedOptions,
          selected_options: selectedOptions,
          add_on_options: check.add_on_options,
        };
      }
      return {
        ...selectedCheck,
        cost: getCheckPrice(check),
        parent_type: check.parent_check_type || undefined,
        must_be_ordered_with_one_of: check.must_be_ordered_with_one_of,
        ordering_type: check.ordering_type,
        price_is_catalog_minimum: resolvePriceIsCatalogMinimum(check),
        check_arguments: updated_check_arguments,
        add_on_options: check.add_on_options,
      };
    }
    return selectedCheck;
  });

  const requiredChecks = userSelectedChecks.flatMap(
    (c) => c.required_check_types || []
  );

  const requiredCheckTypesToOrder = availableChecks.filter(
    ({ type }) =>
      requiredChecks.includes(type) &&
      !selectedChecks.find(({ key }) => key === type)
  );

  if (requiredCheckTypesToOrder.length === 0) {
    return userSelectedChecks;
  }

  const requiredCheckTypesKeys = requiredCheckTypesToOrder.map(
    (requiredCheck) => ({
      key: requiredCheck.type,
      name: requiredCheck.name,
      cost: getCheckPrice(requiredCheck),
      currency: requiredCheck.currency,
      check_arguments: {},
      parent_type: requiredCheck.parent_check_type || undefined,
      must_be_ordered_with_one_of: requiredCheck.must_be_ordered_with_one_of,
      ordering_type: check.ordering_type,
      price_is_catalog_minimum: requiredCheck.price_is_catalog_minimum,
    })
  );

  return [...userSelectedChecks, ...requiredCheckTypesKeys];
}

export function getSelectedChecksExclMutuallyExclusiveInclRequired(
  availableChecks: CheckData[],
  selectedChecks: SelectedChecksType[],
  newlySelectedCheck: CheckData,
  requiredCheckTypes: string[] | undefined
) {
  // filter out selected checks which are mutually exclusive to the newly selected check
  const selectedChecksExcludingExclusiveToNewCheck = selectedChecks.filter(
    ({ key }) =>
      !availableChecks
        .find(({ type }) => type === key)!
        .mutually_exclusive_check_types.includes(newlySelectedCheck.type)
  );
  // filter out checks which the newly selected check is mutually exclusive to
  const newCheckMutuallyExclusiveChecks =
    newlySelectedCheck.mutually_exclusive_check_types;

  const exclusiveChecksRemoved =
    selectedChecksExcludingExclusiveToNewCheck.filter(
      ({ key }) => !newCheckMutuallyExclusiveChecks.includes(key)
    );

  const requiredCheckTypesToOrder = availableChecks.filter(
    ({ type }) =>
      requiredCheckTypes?.includes(type) &&
      !selectedChecks.find(({ key }) => key === type)
  );

  const requiredCheckTypesKeys = requiredCheckTypesToOrder.map(
    (requiredCheck) => ({
      ...requiredCheck,
      key: requiredCheck.type,
      name: requiredCheck.name,
      cost: getCheckPrice(requiredCheck),
      currency: requiredCheck.currency,
      check_arguments: {},
      parent_type: requiredCheck.parent_check_type || undefined,
      price_is_catalog_minimum: requiredCheck.price_is_catalog_minimum,
    })
  );

  return [...exclusiveChecksRemoved, ...requiredCheckTypesKeys];
}

export function getSelectedChecksExclRemovedExclStandalone(
  selectedChecks: SelectedChecksType[],
  removedCheck: CheckData
) {
  const selectedChecksExcludingRemoved = selectedChecks.filter(
    ({ key }) => key !== removedCheck.type
  );

  // filter out standalone checks that are no longer ordered with a required check type
  const excludingStandaloneChecks = selectedChecksExcludingRemoved.filter(
    (selectedCheck) =>
      !isCheckStandaloneDisabled(selectedChecksExcludingRemoved, selectedCheck)
  );

  return excludingStandaloneChecks;
}

/**
 * Calculates the total add-on cost across. Add-on options are
 * deduplicated by key so that mutually inclusive add-ons shared across
 * checks (e.g. CV Review on both Employment and Education) are only
 * counted once per check's cost calculation.
 */
export const getTotalAddOnsCost = (checks: SelectedChecksType[]): number => {
  const allActiveAddOns = uniqBy(
    checks.flatMap((check) =>
      (check.add_on_options ?? []).filter(
        (addon) => check.check_arguments?.[addon.key] === true
      )
    ),
    "key"
  );

  return allActiveAddOns.reduce((acc, addon) => acc + (addon.price ?? 0), 0);
};

export function getOrderCost(
  checks: SelectedChecksType[],
  bundles:
    | SelectedBundlesType[]
    | components["schemas"]["AvailableBundlesList"][],
  packagePrice?: number
) {
  if (packagePrice) {
    return packagePrice.toFixed(2);
  }

  // convert cost to cents for calculation to avoid floating point problems
  const totalAddOnCost = getTotalAddOnsCost(checks) * 100;
  const costOfChecks =
    checks.reduce<number>(
      (total, check) => total + (Number(check.cost) || 0) * 100,
      0
    ) + totalAddOnCost;
  const costOfBundles = bundles.reduce<number>(
    (total, bundle) => total + (Number(bundle.total) || 0) * 100,
    0
  );

  const totalCents = costOfChecks + costOfBundles;
  if (!Number.isFinite(totalCents)) {
    return "0.00";
  }

  return (totalCents / 100).toFixed(2);
}

/**
 * Checks whether any check in the order (individual or package) is a given
 * umbrella parent type whose selected options include a specific child check.
 *
 * The backend returns `value` in different shapes depending on the flow:
 *  - Individual checks (get_check_select_options): `value` is an array, e.g. ["AU_WORKING_WITH_CHILDREN_CHECK_1"]
 *  - Package checks (find_selected_options):       `value` is a plain string, e.g. "AU_WORKING_WITH_CHILDREN_CHECK_1"
 * Both shapes are handled here.
 */
export function orderContainsUmbrellaChildCheck(
  order: Order,
  parentCheckType: string,
  childCheckType: string
): boolean {
  const individualChecks = order.checks.map((c) => ({
    checkType: c.key,
    selectedOptions: c.selected_options,
  }));

  const packageChecks = (order.packageData?.checks ?? []).map((c) => ({
    checkType: c.id,
    selectedOptions: c.selected_options as { value?: string | string[] }[],
  }));

  return [...individualChecks, ...packageChecks].some(
    ({ checkType, selectedOptions }) =>
      checkType === parentCheckType &&
      selectedOptions?.some((opt) => {
        const v = opt.value;
        return Array.isArray(v)
          ? v.includes(childCheckType)
          : v === childCheckType;
      })
  );
}

/** Umbrella parent card under which AU WWCC is ordered in the Client Portal. */
const AU_WWCC_ORDER_REVIEW_PARENT = "PROFESSIONAL_REGULATORY_REGISTER_1";
/** Explicit child check type for Australia Working With Children (ordering flow). */
const AU_WWCC_ORDER_REVIEW_CHILD = "AU_WORKING_WITH_CHILDREN_CHECK_1";

/**
 * True when the order includes AU WWCC under the Professional & Regulatory
 * umbrella, matching the historical `OrderReviewSummaryBox` disclaimer gate
 * (same semantics as `orderContainsUmbrellaChildCheck` for the fixed parent/child pair).
 */
export function orderIncludesAuWwcc(order: Order): boolean {
  return orderContainsUmbrellaChildCheck(
    order,
    AU_WWCC_ORDER_REVIEW_PARENT,
    AU_WWCC_ORDER_REVIEW_CHILD
  );
}

const CREDIT_UMBRELLA = "CREDIT_REPORT_1";
const CRR_CARD = "CRIMINAL_RECORD_REPORT_1";
const MVR_UMBRELLA = "MOTOR_VEHICLE_RECORD_1";
const US_EMPLOYMENT_CONSUMER_REPORT_CHECK_TYPES = new Set([
  "US_CREDIT_REPORT_1",
  "US_MOTOR_VEHICLE_RECORD_1",
  "US_NATIONAL_CRIMINAL_DATABASE_1",
  "US_NATIONAL_SEX_OFFENDER_REGISTRY_1",
  "US_FEDERAL_CRIMINAL_COURT_RECORD_CHECK_1",
  "US_COUNTY_CRIMINAL_COURT_RECORD_CHECK_1",
]);
const ADDRESS_BASED_UMBRELLA_ORDERING_TYPES = new Set([
  "SINGLE_REGION",
  "CURRENT_REGION",
  "MULTI_REGION",
]);

function normalizeExplicitCheckTypes(
  checkArguments: Record<string, unknown>
): string[] {
  const explicit = checkArguments.explicit_check_type;
  if (Array.isArray(explicit)) {
    return explicit.map(String);
  }
  if (typeof explicit === "string") {
    return [explicit];
  }
  return [];
}

function umbrellaArgumentsRequireEmploymentConsumerReportCertification(
  umbrellaType: string,
  checkArguments: Record<string, unknown>
): boolean {
  const orderingType = checkArguments.ordering_type;
  if (
    typeof orderingType === "string" &&
    ADDRESS_BASED_UMBRELLA_ORDERING_TYPES.has(orderingType)
  ) {
    return false;
  }
  if (
    orderingType !== undefined &&
    orderingType !== null &&
    orderingType !== "EXPLICIT_ORDERING"
  ) {
    return false;
  }

  const explicitTypes = normalizeExplicitCheckTypes(checkArguments);
  if (umbrellaType === CREDIT_UMBRELLA) {
    return explicitTypes.includes("US_CREDIT_REPORT_1");
  }
  if (umbrellaType === CRR_CARD) {
    return explicitTypes.some((checkType) =>
      US_EMPLOYMENT_CONSUMER_REPORT_CHECK_TYPES.has(checkType)
    );
  }
  if (umbrellaType === MVR_UMBRELLA) {
    return explicitTypes.includes("US_MOTOR_VEHICLE_RECORD_1");
  }
  return false;
}

function checkTypesRequireEmploymentConsumerReportCertification(
  checkTypesWithArguments: Record<string, Record<string, unknown>>
): boolean {
  return Object.entries(checkTypesWithArguments).some(
    ([checkType, checkArguments]) => {
      if (US_EMPLOYMENT_CONSUMER_REPORT_CHECK_TYPES.has(checkType)) {
        return true;
      }
      if (
        checkType === CRR_CARD ||
        checkType === CREDIT_UMBRELLA ||
        checkType === MVR_UMBRELLA
      ) {
        return umbrellaArgumentsRequireEmploymentConsumerReportCertification(
          checkType,
          checkArguments ?? {}
        );
      }
      return false;
    }
  );
}

export function orderRequiresEmploymentConsumerReportCertification(
  order: Order,
  user?: UserDetails
): boolean {
  const { check_types_with_arguments: checkTypesWithArguments } =
    orderContextToSubmitOrderPayload(order, user);

  const checksNeedCertification =
    checkTypesRequireEmploymentConsumerReportCertification(
      checkTypesWithArguments as Record<string, Record<string, unknown>>
    );

  if (!checksNeedCertification) {
    return false;
  }

  if (!user) {
    return true;
  }

  const groupId = getCustomerGroupId(order, user);
  const permissiblePurpose = user.customer_groups?.find(
    (group) => group.id === groupId
  )?.permissible_purpose;

  if (permissiblePurpose === undefined) {
    return true;
  }

  if (permissiblePurpose !== "EMPLOYMENT") {
    return false;
  }

  return true;
}

const REGULATED_ROLES_CARD = "UK_CRIMINAL_REGULATED_ROLES_1";
const UK_CHECK_TYPES = new Set([
  "BASIC_UK_DBS_1",
  "BASIC_UK_DS_1",
  "ENHANCED_UK_DBS_1",
  "STANDARD_UK_DBS_1",
  "UK_DS_LVL2_1",
  "UK_DS_LVL2_BARRED_1",
  "NIR_CRIMINAL_RECORD_1",
  "NIR_CRIMINAL_RECORD_2",
  "UK_ACCESS_NI_BASIC_1",
  "UK_ACCESS_NI_STANDARD_1",
  "UK_ACCESS_NI_ENHANCED_1",
]);

export function getOptionCheckTypes(
  opt: components["schemas"]["UmbrellaCheckSelectOption"]
): string[] {
  return Array.isArray(opt.value) ? opt.value : [String(opt.value)];
}

/**
 * After a card's options are updated, resolve cross-card conflicts between
 * the Criminal Record Check card and the UK Regulated Roles card.
 *
 * "Last selection wins": if the user just selected a UK check in one card
 * that conflicts with an option in the other card, the conflicting option
 * is removed from the other card. If removing all options from the other
 * card leaves it empty, the entire card is removed from the order.
 */
export function resolveUkDbsCrossCardConflicts(
  checks: SelectedChecksType[],
  updatedCardType: string,
  newSelectedOptions?: components["schemas"]["UmbrellaCheckSelectOption"][]
): SelectedChecksType[] {
  if (
    updatedCardType !== CRR_CARD &&
    updatedCardType !== REGULATED_ROLES_CARD
  ) {
    return checks;
  }
  if (!newSelectedOptions || newSelectedOptions.length === 0) return checks;

  const conflictingTypes = new Set<string>();
  for (const opt of newSelectedOptions) {
    const types = getOptionCheckTypes(opt);
    if (!types.some((t) => UK_CHECK_TYPES.has(t))) continue;
    for (const exclusive of opt.mutually_exclusive_check_types ?? []) {
      conflictingTypes.add(exclusive);
    }
  }
  if (conflictingTypes.size === 0) return checks;

  const otherCardType =
    updatedCardType === CRR_CARD ? REGULATED_ROLES_CARD : CRR_CARD;

  return checks
    .map((check) => {
      if (check.key !== otherCardType) return check;

      const options = check.selectedOptions ?? check.selected_options ?? [];
      const filtered = options.filter((opt) => {
        const types = getOptionCheckTypes(opt);
        return !types.some((t) => conflictingTypes.has(t));
      });

      if (filtered.length === options.length) return check;
      if (filtered.length === 0) return null;

      const explicit = filtered.flatMap(getOptionCheckTypes);
      return {
        ...check,
        selectedOptions: filtered,
        selected_options: filtered,
        check_arguments: {
          ...check.check_arguments,
          explicit_check_type: explicit,
        },
      };
    })
    .filter((c): c is SelectedChecksType => c !== null);
}

export function getOptionHasOrderingErrorForCustomerGroupFromChecks(
  checks: SelectedChecksType[]
): components["schemas"]["CheckOrderingError"][] {
  return checks.flatMap((check) =>
    Array.isArray(check.selected_options)
      ? check.selected_options
          .map((opt) => opt.option_has_ordering_error_for_customer_group)
          .filter(Boolean)
      : []
  );
}

function humanizeSnakeCaseToken(token: string): string {
  const withoutVersion = token.replace(/_\d+$/, "");
  return withoutVersion
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function labelForSubmitOrderErrorPath(path: string[]): string {
  if (path[0] === "check_types_with_arguments" && path.length >= 2) {
    return humanizeSnakeCaseToken(path[path.length - 1]);
  }

  return path
    .filter((segment) => segment !== "check_types_with_arguments")
    .map(humanizeSnakeCaseToken)
    .join(" ");
}

/** DRF validation bodies can nest check-type keys under field names. */
export function flattenSubmitOrderErrorBody(
  body: Record<string, unknown>
): { label: string; message: string }[] {
  const messages: { label: string; message: string }[] = [];

  const walk = (value: unknown, path: string[]) => {
    if (typeof value === "string") {
      const label = labelForSubmitOrderErrorPath(path);
      messages.push({
        label: label || "Error",
        message: value,
      });
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => walk(item, path));
      return;
    }

    if (value && typeof value === "object") {
      Object.entries(value as Record<string, unknown>).forEach(
        ([key, nested]) => walk(nested, [...path, key])
      );
    }
  };

  Object.entries(body).forEach(([key, value]) => walk(value, [key]));
  return messages;
}

/**
 * Parses structured error bodies from the submit-order API and displays
 * field-level toast messages when possible, falling back to a generic message.
 */
export function showSubmitOrderError(e: unknown, t: TFunction) {
  const errBody = (e as any)?.body;
  const errStatus = (e as any)?.status;

  // DAT-1730: 402 with `order_blocked_unpaid_invoices` indicates the
  // daily-invoiced CLIENT-pay customer hit the billing gate (LD flag on +
  // unpaid invoice on the resolved billing profile). Render a richer toast
  // including count + outstanding balance + currency rather than the generic
  // field-keyed shape below.
  if (
    errStatus === 402 &&
    errBody &&
    typeof errBody === "object" &&
    (errBody as any).error === "order_blocked_unpaid_invoices"
  ) {
    const isCollectionsHold = (errBody as any).reason === "COLLECTIONS";
    const hasAmount = (errBody as any).total_outstanding_amount != null;
    const msg =
      isCollectionsHold && !hasAmount
        ? t(
            "Ordering is paused due to non-payment. Submit payment for the full overdue balance to restore ordering access."
          )
        : isCollectionsHold
          ? t(
              "Ordering is paused due to non-payment ({{amount}} {{currency}} overdue). Submit payment for the full overdue balance to restore ordering access.",
              {
                amount: (errBody as any).total_outstanding_amount,
                currency: (errBody as any).currency,
              }
            )
          : t(
              "Ordering is paused. There is currently {{count}} unpaid invoice(s) totaling {{amount}} {{currency}}. Please pay the balance to resume ordering immediately.",
              {
                count: (errBody as any).unpaid_invoice_count,
                amount: (errBody as any).total_outstanding_amount,
                currency: (errBody as any).currency,
              }
            );
    showToast(msg, {
      type: "error",
      timeout: calculateToastTimeout(msg),
    });
    return;
  }

  if (Array.isArray(errBody)) {
    errBody.forEach((msg: string) =>
      showToast(msg, {
        type: "error",
        timeout: calculateToastTimeout(msg),
      })
    );
    return;
  }

  if (errBody && typeof errBody === "object" && !Array.isArray(errBody)) {
    const flattened = flattenSubmitOrderErrorBody(
      errBody as Record<string, unknown>
    );

    if (flattened.length > 0) {
      flattened.forEach(({ label, message }) =>
        showToast(`${label}: ${message}`, {
          type: "error",
          timeout: calculateToastTimeout(message),
        })
      );
      return;
    }
  }

  console.error("Error", e);
  showToast(t("Something went wrong, try again"), { type: "error" });
}
